import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import {
  ONLINE_SESSION_TIMEOUT_MS,
  SESSION_WARNING_WINDOW_MS,
} from '../../auth/sessionConfig';
import { useI18n } from '../../i18n/I18nContext';
import './SessionExpiryNotice.css';

const RADIUS = 22;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function formatRemaining(ms: number) {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

const SessionExpiryNotice: React.FC = () => {
  const { auth, backendReachable, refreshSessionActivity } = useAuth();
  const { t } = useI18n();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!auth?.authenticated || auth.authExpired || !backendReachable) {
      return;
    }
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [auth?.authenticated, auth?.authExpired, backendReachable]);

  const remainingMs = useMemo(() => {
    if (!auth?.authenticated || auth.authExpired || !backendReachable || !auth.lastActivityAt) {
      return null;
    }
    const startedAt = new Date(auth.lastActivityAt).getTime();
    if (Number.isNaN(startedAt)) {
      return null;
    }
    return Math.max(0, ONLINE_SESSION_TIMEOUT_MS - (now - startedAt));
  }, [auth, backendReachable, now]);

  if (remainingMs === null || remainingMs > SESSION_WARNING_WINDOW_MS) {
    return null;
  }

  const warningElapsed = SESSION_WARNING_WINDOW_MS - remainingMs;
  const progress = Math.max(0, Math.min(1, warningElapsed / SESSION_WARNING_WINDOW_MS));
  const dashOffset = CIRCUMFERENCE * (1 - progress);

  return (
    <div className="session-expiry-notice" role="status" aria-live="polite">
      <div className="session-expiry-ring" aria-hidden="true">
        <svg viewBox="0 0 56 56">
          <circle className="session-expiry-ring-track" cx="28" cy="28" r={RADIUS} />
          <circle
            className="session-expiry-ring-progress"
            cx="28"
            cy="28"
            r={RADIUS}
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
          />
        </svg>
        <span>{formatRemaining(remainingMs)}</span>
      </div>
      <div className="session-expiry-copy">
        <strong>{t('session.warningTitle')}</strong>
        <p>{t('session.warningBody')}</p>
      </div>
      <button
        type="button"
        className="session-expiry-action"
        onClick={() => void refreshSessionActivity()}
      >
        {t('session.staySignedIn')}
      </button>
    </div>
  );
};

export default SessionExpiryNotice;
